import { useAuthStore } from "@features/auth/store/authStore";
import OnlineIndicator from "@features/presence/components/OnlineIndicator";
import { getProfileImage, useProfile } from "@shared/hooks/useProfile";
import { User } from "@shared/types/user";
import theme from "@shared/utils/themeColors";
import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { ChatWindow, useChitChat } from "../context/ChitChatContext";
import { useConversations } from "../context/ConversationsContext";
import { useGroupConversations } from "../hooks/useGroupConversations";
import { useMessages } from "../hooks/useMessages";
import {
  buildUserMap,
  fetchGroupParticipantsUserMap,
} from "../services/userMap.service";
import { useUserMapStore } from "../store/userMapStore";
import GroupTypingIndicator from "./GroupTypingIndicator";
import MessageInput from "./MessageInput";
import MessageList from "./MessageList";

interface ChitChatWindowProps {
  chatWindow: ChatWindow;
}

const ChitChatWindow: React.FC<ChitChatWindowProps> = ({ chatWindow }) => {
  const navigate = useNavigate();
  const { closeChatWindow, minimizeChatWindow } = useChitChat();
  const { user } = useProfile();
  const { conversations: rawConversations, userProfiles } = useConversations();
  const currentUser = useAuthStore((state) => state.user);
  const { groups } = useGroupConversations();
  const [userMap, setUserMap] = useState<Record<string, User>>({});
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const userProfileIds = useMemo(
    () => Object.keys(userProfiles),
    [userProfiles],
  );

  const conversations = useMemo(
    () =>
      rawConversations.map((conv) => ({
        ...conv,
        type:
          conv.type || (conv.participants.length === 2 ? "direct" : "group"),
      })),
    [rawConversations],
  );

  const selectedConversation = useMemo(
    () => conversations.find((c) => c.id === chatWindow.conversationId),
    [conversations, chatWindow.conversationId],
  );

  const userMapLoadedRef = React.useRef(false);
  const lastConversationIdRef = React.useRef<string | null>(null);
  const fetchUserFromStore = useUserMapStore((state) => state.fetchUser);

  useEffect(() => {
    if (
      lastConversationIdRef.current === chatWindow.conversationId &&
      userMapLoadedRef.current
    ) {
      return;
    }

    if (
      selectedConversation?.type === "group" &&
      selectedConversation.id &&
      currentUser
    ) {
      lastConversationIdRef.current = chatWindow.conversationId;
      userMapLoadedRef.current = true;
      fetchGroupParticipantsUserMap(selectedConversation.id)
        .then((map) => setUserMap(map))
        .catch(console.error);
    } else if (userProfileIds.length > 0 && !userMapLoadedRef.current) {
      lastConversationIdRef.current = chatWindow.conversationId;
      userMapLoadedRef.current = true;
      buildUserMap(userProfileIds, currentUser || undefined).then(setUserMap);
    }
  }, [
    chatWindow.conversationId,
    selectedConversation,
    userProfileIds,
    currentUser,
  ]);

  useEffect(() => {
    if (selectedConversation?.type === "direct") {
      const otherUserId = selectedConversation.participants.find(
        (id) => id !== user?.id,
      );
      if (otherUserId && !userMap[otherUserId] && !userProfiles[otherUserId]) {
        fetchUserFromStore(otherUserId).then((fetchedUser) => {
          if (fetchedUser) {
            setUserMap((prev) => ({ ...prev, [otherUserId]: fetchedUser }));
          }
        });
      }
    }
  }, [
    selectedConversation,
    user?.id,
    userMap,
    userProfiles,
    fetchUserFromStore,
  ]);

  useEffect(() => {
    if (selectedConversation && selectedConversation.type === "direct") {
      const otherUserId = selectedConversation.participants.find(
        (id) => id !== user?.id,
      );
      if (otherUserId && userMap[otherUserId]) {
        setSelectedUser(userMap[otherUserId]);
      }
    } else {
      setSelectedUser(null);
    }
  }, [selectedConversation, user?.id, userMap]);

  const {
    messages,
    loading: messagesLoading,
    sendMessage,
    deleteMessage,
    editMessage,
    updateTypingStatus,
    hasMore,
    loadMoreMessages: loadMore,
    isTyping,
  } = useMessages(chatWindow.conversationId, selectedUser, userMap);

  const handleDeleteMessage = async (messageId: string) => {
    try {
      await deleteMessage(messageId);
      toast.success("Message deleted successfully");
    } catch (error) {
      console.error("Error deleting message:", error);
      toast.error("Failed to delete message. Please try again.");
    }
  };

  const handleEditMessage = async (messageId: string, newContent: string) => {
    try {
      await editMessage(messageId, newContent);
    } catch (error: any) {
      const errorMessage =
        error?.message === 'Edit time limit exceeded'
          ? 'You can only edit messages within 5 minutes of sending.'
          : 'Failed to edit message. Please try again.';
      toast.error(errorMessage);
    }
  };

  const isGroup = selectedConversation?.type === "group";
  const group = isGroup
    ? groups.find((g) => g.id === chatWindow.conversationId)
    : null;

  const getHeaderImage = () => {
    if (isGroup) {
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(
        group?.title || "Group",
      )}&background=6366f1&color=fff`;
    }
    if (selectedUser?.profile) {
      return getProfileImage(selectedUser.profile);
    }
    if (selectedConversation?.type === "direct") {
      const otherUserId = selectedConversation.participants.find(
        (id) => id !== user?.id,
      );
      if (otherUserId && userProfiles[otherUserId]) {
        return getProfileImage(userProfiles[otherUserId]);
      }
    }
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(
      chatWindow.title || "User",
    )}&background=random`;
  };

  const getHeaderTitle = () => {
    if (isGroup && group) {
      return group.title || "Group Chat";
    }
    if (selectedUser?.profile?.name) {
      return selectedUser.profile.name;
    }
    if (selectedConversation?.type === "direct") {
      const otherUserId = selectedConversation.participants.find(
        (id) => id !== user?.id,
      );
      if (otherUserId && userProfiles[otherUserId]?.name) {
        return userProfiles[otherUserId].name;
      }
    }
    return chatWindow.title || "Chat";
  };

  const getTypingStatus = () => {
    if (!isTyping || typeof isTyping !== "object") return null;

    const typingUserIds = Object.entries(isTyping)
      .filter(([userId, typing]) => typing && userId !== user?.id)
      .map(([userId]) => userId);

    if (typingUserIds.length === 0) return null;

    if (isGroup) {
      const typingUsers = typingUserIds
        .map((userId) => ({
          id: userId,
          name: userProfiles[userId]?.name || "",
          photo: getProfileImage(userProfiles[userId]),
        }))
        .filter((u) => u.name);
      return typingUsers.length > 0 ? typingUsers : null;
    }

    return "Typing...";
  };

  const typingStatus = getTypingStatus();

  return (
    <div className="flex flex-col w-[calc(100vw-1rem)] md:w-[350px] h-[500px] max-h-[calc(100vh-120px)] bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-300 dark:border-gray-700 overflow-hidden">
      <div className={`flex items-center p-3 border-b ${theme.border.primary}`}>
        <div
          className={`flex items-center flex-1 min-w-0 ${!isGroup ? "cursor-pointer" : ""
            }`}
          onClick={() => {
            if (!isGroup) {
              const otherUserId =
                selectedUser?.id ||
                (selectedConversation?.type === "direct"
                  ? selectedConversation.participants.find(
                    (id) => id !== user?.id,
                  )
                  : null) ||
                chatWindow.user?.id;
              if (otherUserId) {
                navigate(`/community/profile/${otherUserId}`);
              }
            }
          }}
        >
          <div className="flex-shrink-0 mr-3">
            {isGroup ? (
              <span
                className="flex items-center justify-center w-9 h-9 rounded-full"
                style={{ backgroundColor: group?.color || "#6366f1" }}
              >
                <svg
                  width="20"
                  height="20"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="white"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M7 17l2-10m4 10l2-10M5 9h14M5 15h14"
                  />
                </svg>
              </span>
            ) : (
              <div className="relative">
                <img
                  src={getHeaderImage()}
                  alt={getHeaderTitle()}
                  className="w-9 h-9 rounded-full object-cover hover:opacity-80 transition-opacity"
                />
                <OnlineIndicator
                  userId={selectedUser?.id ||
                    (selectedConversation?.type === "direct"
                      ? selectedConversation.participants.find((id) => id !== user?.id)
                      : undefined)}
                  className="absolute bottom-0 right-0"
                />
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1 flex items-center">
            <h3
              className={`font-semibold ${theme.text.primary} truncate ${!isGroup ? "hover:underline" : ""
                }`}
            >
              {getHeaderTitle()}
            </h3>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => minimizeChatWindow(chatWindow.conversationId)}
            className={`p-1 ${theme.text.mutedHover} rounded`}
            aria-label="Minimize chat"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 12H4"
              />
            </svg>
          </button>
          <button
            onClick={() => closeChatWindow(chatWindow.conversationId)}
            className={`p-1 ${theme.text.mutedHover} rounded`}
            aria-label="Close chat"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      <div className="flex-grow flex flex-col overflow-y-auto overflow-x-visible relative pb-3">
        {messages.length === 0 && !messagesLoading ? (
          <div className="flex-1 flex items-center justify-center p-4">
            <p className={`text-center ${theme.text.muted}`}>
              Start a conversation with {getHeaderTitle()}!
            </p>
          </div>
        ) : (
          <MessageList
            messages={messages}
            loading={messagesLoading}
            hasMore={hasMore}
            onLoadMore={loadMore}
            onDeleteMessage={handleDeleteMessage}
            onEditMessage={handleEditMessage}
            user={user}
            selectedUser={selectedUser}
            userProfiles={userMap}
            isGroup={isGroup}
          />
        )}
        {Array.isArray(typingStatus) && typingStatus.length > 0 && (
          <GroupTypingIndicator typingUsers={typingStatus} />
        )}
      </div>

      {!isGroup && (
        <div className="flex-shrink-0 h-4 px-4 flex items-center">
          <p className="text-xs text-amber-500">
            {typeof typingStatus === "string"
              ? `${getHeaderTitle()} is typing...`
              : "\u00A0"}
          </p>
        </div>
      )}

      <MessageInput
        onSendMessage={(content, type, mediaUrl) =>
          sendMessage(content, type, mediaUrl)
        }
        updateTypingStatus={updateTypingStatus}
      />
    </div>
  );
};

export default ChitChatWindow;
